import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { notifier } from 'components/Toaster';
import { storageKeys } from 'utilities/constants';

const PdfJobContext = createContext();
const GENERIC_PDF_ERROR_MESSAGE = 'We could not generate the PDF right now. Please try again.';
const PDF_CANCELLED_MESSAGE = 'PDF generation was cancelled.';
const ACTIVE_JOB_STATUSES = ['FETCHING', 'SENDING', 'PROCESSING', 'QUEUED', 'RECOVERING'];

// Registry of recovery builders keyed by `recoveryHint.kind`. A caller (e.g. the
// quiz PDF page) calls `registerRecoveryBuilder('quiz', (hint, helpers) => ...)`
// once on mount; on page reload, the context uses the registered builder to
// rebuild the `run` / `downloadFn` closures for jobs that were active when the
// browser was last open or that the server reports as still in progress.
const recoveryBuilders = new Map();
export const registerPdfJobRecoveryBuilder = (kind, builder) => {
  if (!kind || typeof builder !== 'function') return () => {};
  recoveryBuilders.set(kind, builder);
  return () => {
    if (recoveryBuilders.get(kind) === builder) recoveryBuilders.delete(kind);
  };
};

const getPdfErrorMessage = (error, fallback = GENERIC_PDF_ERROR_MESSAGE) => {
  if (!error) return fallback;
  if (typeof error === 'string') return error || fallback;
  return error.message || error.error || fallback;
};

export const usePdfJob = () => useContext(PdfJobContext);

export const PdfJobProvider = ({ children }) => {
  const [jobs, setJobs] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const queueRef = useRef([]);
  const isProcessingRef = useRef(false);
  const cancelledJobIdsRef = useRef(new Set());
  const abortControllersRef = useRef({});

  useEffect(() => {
    return () => {
      Object.values(abortControllersRef.current).forEach((controller) => controller?.abort?.());
      abortControllersRef.current = {};
      queueRef.current = [];
      isProcessingRef.current = false;
    };
  }, []);

  // ─── Process next job in the queue ────────────────────────────────────────
  // All PDF work goes through the `run` function provided by the caller.
  // The context only manages lifecycle (queued → running → done/failed).
  const processNextJob = useCallback(async () => {
    if (isProcessingRef.current || queueRef.current.length === 0) return;

    isProcessingRef.current = true;
    setIsProcessing(true);
    const jobData = queueRef.current[0];
    const { id, title, run } = jobData;
    const abortController = typeof AbortController !== 'undefined' ? new AbortController() : null;
    if (abortController) {
      abortControllersRef.current[id] = abortController;
    }

    try {
      if (typeof run !== 'function') {
        throw new Error('PDF job must provide a `run` function.');
      }

      await run({
        job: jobData,
        updateJob: (updates) => updateJob(id, updates),
        isCancelled: () => cancelledJobIdsRef.current.has(id),
        signal: abortController?.signal,
      });

      finishJob(id);
    } catch (error) {
      if (cancelledJobIdsRef.current.has(id)) {
        finishJob(id);
        return;
      }
      console.error('PDF export failed', { title, error });
      const errorMessage = getPdfErrorMessage(error, GENERIC_PDF_ERROR_MESSAGE);
      updateJob(id, { status: 'FAILED', error: errorMessage });
      notifier.error(`${title}: ${errorMessage}`);
      finishJob(id);
    }
  }, []);

  const finishJob = (id) => {
    if (id) {
      delete abortControllersRef.current[id];
      queueRef.current = queueRef.current.filter((job) => job.id !== id);
    } else {
      queueRef.current.shift();
    }
    isProcessingRef.current = false;
    setIsProcessing(false);
  };

  useEffect(() => {
    if (!isProcessing && queueRef.current.length > 0) {
      processNextJob();
    }
  }, [isProcessing, processNextJob]);

  const updateJob = (id, updates) => {
    queueRef.current = queueRef.current.map((job) =>
      job.id === id ? { ...job, ...updates } : job
    );
    setJobs((prev) => prev.map((job) => (job.id === id ? { ...job, ...updates } : job)));
  };

  const downloadTrackedJobPdf = async (job) => {
    if (!job) return false;

    if (typeof job.downloadFn === 'function') {
      try {
        const downloaded = await job.downloadFn(job);
        if (downloaded) updateJob(job.id, { downloaded: true });
        return Boolean(downloaded);
      } catch (error) {
        console.error('Failed to download tracked PDF job', { job, error });
        notifier.error(`Download failed. Please try again.`);
        return false;
      }
    }

    notifier.error(
      'PDF is ready, but the download link is missing. Please try Download PDF from the chapter menu.'
    );
    return false;
  };

  const addJob = ({ id, title, filename, run, downloadFn, recoveryHint }) => {
    // If a job with the same title is already active, force-cancel it.
    const existingActiveJob =
      jobs.find((j) => j.title === title && ACTIVE_JOB_STATUSES.includes(j.status)) ||
      queueRef.current.find((j) => j.title === title && ACTIVE_JOB_STATUSES.includes(j.status));

    if (existingActiveJob) {
      console.warn(
        `Force-cancelling stale job "${title}" (${existingActiveJob.id}) to start fresh.`
      );
      cancelledJobIdsRef.current.add(existingActiveJob.id);

      if (abortControllersRef.current[existingActiveJob.id]) {
        abortControllersRef.current[existingActiveJob.id].abort();
        delete abortControllersRef.current[existingActiveJob.id];
      }

      setJobs((prev) => prev.filter((j) => j.id !== existingActiveJob.id));
      queueRef.current = queueRef.current.filter((j) => j.id !== existingActiveJob.id);

      if (isProcessingRef.current && queueRef.current[0]?.id === existingActiveJob.id) {
        isProcessingRef.current = false;
        setIsProcessing(false);
      }
    }

    const jobId = id || Date.now().toString();
    const newJob = {
      id: jobId,
      title,
      status: 'QUEUED',
      progress: 0,
      filename,
      run,
      downloadFn,
      // recoveryHint is the only serializable description of how to rebuild
      // the run/downloadFn closures after a page reload. Shape is owned by
      // the caller (e.g. { kind: 'quiz', quizId } for per-quiz PDFs).
      recoveryHint: recoveryHint || null,
    };

    // Clean up old completed/failed/cancelled jobs with the same title
    setJobs((prev) => {
      const cleaned = prev.filter(
        (j) => !(j.title === title && !ACTIVE_JOB_STATUSES.includes(j.status))
      );
      return [newJob, ...cleaned];
    });
    queueRef.current.push(newJob);

    // Trigger processing if idle
    if (!isProcessingRef.current) {
      processNextJob();
    }
  };

  const clearJobs = () => {
    setJobs((prev) => prev.filter((j) => ACTIVE_JOB_STATUSES.includes(j.status)));
  };

  const retryJob = useCallback(
    (jobId) => {
      const failedJob =
        jobs.find((j) => j.id === jobId) || queueRef.current.find((j) => j.id === jobId);
      if (!failedJob) return;
      if (ACTIVE_JOB_STATUSES.includes(failedJob.status)) return;
      if (typeof failedJob.run !== 'function') {
        notifier.error('This PDF job cannot be retried here. Please start the export again.');
        return;
      }

      cancelledJobIdsRef.current.delete(jobId);
      const resetJob = {
        ...failedJob,
        status: 'QUEUED',
        progress: 0,
        error: '',
        downloaded: false,
      };
      setJobs((prev) => prev.map((j) => (j.id === jobId ? resetJob : j)));
      queueRef.current = queueRef.current.filter((j) => j.id !== jobId);
      queueRef.current.push(resetJob);
      if (!isProcessingRef.current) processNextJob();
    },
    [jobs, processNextJob]
  );

  const removeJob = (id) => {
    const job =
      jobs.find((item) => item.id === id) || queueRef.current.find((item) => item.id === id);
    const isActiveJob = job && ACTIVE_JOB_STATUSES.includes(job.status);

    if (isActiveJob) {
      cancelledJobIdsRef.current.add(id);
      updateJob(id, { status: 'CANCELLED', progress: 0, error: PDF_CANCELLED_MESSAGE });

      if (abortControllersRef.current[id]) {
        abortControllersRef.current[id].abort();
      }

      if (queueRef.current[0]?.id !== id) {
        queueRef.current = queueRef.current.filter((queuedJob) => queuedJob.id !== id);
      } else {
        finishJob(id);
      }

      return;
    }

    setJobs((prev) => prev.filter((j) => j.id !== id));
    if (!isActiveJob) {
      queueRef.current = queueRef.current.filter((j) => j.id !== id);
    }
  };

  // Persist jobs to localStorage (only serializable fields). Skipped until
  // the recovery effect has had a chance to read existing storage — otherwise
  // the first commit (jobs=[]) would wipe localStorage before any persisted
  // jobs are restored, and any QUEUED jobs from before the reload would be
  // lost forever (they're not on the server, only in localStorage).
  const hasRecoveredRef = useRef(false);
  useEffect(() => {
    if (!hasRecoveredRef.current) return;
    try {
      const serializableJobs = jobs.map(({ run, downloadFn, ...rest }) => rest);
      localStorage.setItem(storageKeys.PDF_JOBS, JSON.stringify(serializableJobs));
    } catch (e) {
      // localStorage write failure is non-critical
    }
  }, [jobs]);

  // Pending list of active jobs from previous browser session that still need
  // a recovery builder to be registered before they can be re-attached. The
  // caller pages register their builders during their own mount, which may
  // happen after this provider mounts.
  const pendingRecoveryRef = useRef([]);

  const tryRebuildJob = useCallback((job) => {
    const hint = job?.recoveryHint;
    if (!hint?.kind) return null;
    const builder = recoveryBuilders.get(hint.kind);
    if (!builder) return null;
    try {
      // previousStatus lets the builder choose between starting a new server
      // job (QUEUED never reached the server) and attaching to an existing one
      // (SENDING/PROCESSING/RECOVERING already POSTed to the server).
      const built = builder(hint, {
        jobId: job.id,
        job,
        previousStatus: job.status || 'QUEUED',
      });
      if (!built || typeof built.run !== 'function') return null;
      return {
        ...job,
        run: built.run,
        downloadFn: built.downloadFn || job.downloadFn,
        status: 'RECOVERING',
        progress: job.progress || 0,
        error: '',
      };
    } catch (e) {
      console.error('PDF job recovery builder failed', e);
      return null;
    }
  }, []);

  const enqueueRecovered = useCallback(
    (rebuilt) => {
      setJobs((prev) => {
        const without = prev.filter((j) => j.id !== rebuilt.id);
        return [rebuilt, ...without];
      });
      queueRef.current = queueRef.current.filter((j) => j.id !== rebuilt.id);
      queueRef.current.push(rebuilt);
      if (!isProcessingRef.current) processNextJob();
    },
    [processNextJob]
  );

  // Try to rebuild any pending-recovery jobs after a builder registers.
  // Polled on every render so the latest recoveryBuilders map is seen.
  useEffect(() => {
    if (pendingRecoveryRef.current.length === 0) return;
    const remaining = [];
    for (const job of pendingRecoveryRef.current) {
      const rebuilt = tryRebuildJob(job);
      if (rebuilt) enqueueRecovered(rebuilt);
      else remaining.push(job);
    }
    pendingRecoveryRef.current = remaining;
  });

  // On mount, recover persisted jobs. Active jobs with a recoveryHint get
  // re-attached (now or once their builder registers). Jobs without a hint
  // fall back to the old FAILED-on-reload behaviour.
  useEffect(() => {
    try {
      const savedJobsStr = localStorage.getItem(storageKeys.PDF_JOBS);
      if (!savedJobsStr) return;
      const parsed = JSON.parse(savedJobsStr);
      const recoveredJobs = [];
      const toRebuild = [];
      for (const job of parsed) {
        if (!ACTIVE_JOB_STATUSES.includes(job.status)) {
          recoveredJobs.push(job);
          continue;
        }
        if (job.recoveryHint) {
          const rebuilt = tryRebuildJob(job);
          if (rebuilt) {
            recoveredJobs.push(rebuilt);
            queueRef.current.push(rebuilt);
          } else {
            // Builder not registered yet — keep job visible as RECOVERING
            // and retry once a builder appears.
            recoveredJobs.push({ ...job, status: 'RECOVERING', error: '' });
            toRebuild.push(job);
          }
        } else {
          recoveredJobs.push({
            ...job,
            status: 'FAILED',
            error: 'Please try generating again.',
          });
        }
      }
      if (recoveredJobs.length > 0) setJobs(recoveredJobs);
      pendingRecoveryRef.current = toRebuild;
      if (queueRef.current.length > 0 && !isProcessingRef.current) {
        processNextJob();
      }
    } catch (e) {
      console.error('Failed to load PDF jobs from localStorage', e);
    } finally {
      // Allow the persistence effect to start writing now that we've read.
      hasRecoveredRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Public: caller can push server-discovered jobs into the box on mount.
  // Each `serverJob` must include a recoveryHint. Already-known jobs (by id
  // or by matching recoveryHint identity) are skipped.
  const bootstrapServerJobs = useCallback(
    (serverJobs) => {
      if (!Array.isArray(serverJobs) || serverJobs.length === 0) return;
      const knownIds = new Set([...jobs.map((j) => j.id), ...queueRef.current.map((j) => j.id)]);
      const hintKey = (h) =>
        h ? `${h.kind}:${h.quizId || ''}:${h.chapterId || ''}:${h.category || ''}` : '';
      const knownHints = new Set(
        [...jobs, ...queueRef.current].map((j) => hintKey(j.recoveryHint)).filter(Boolean)
      );

      for (const serverJob of serverJobs) {
        if (!serverJob?.recoveryHint) continue;
        const jobId = serverJob.id || `srv-${hintKey(serverJob.recoveryHint)}`;
        if (knownIds.has(jobId)) continue;
        if (knownHints.has(hintKey(serverJob.recoveryHint))) continue;
        const skeleton = {
          id: jobId,
          title: serverJob.title || 'PDF',
          // Server-discovered jobs already exist on the server — pass
          // PROCESSING so the recovery builder picks startMode 'attach', not
          // 'start' (which would POST and create a duplicate).
          status: 'PROCESSING',
          progress: serverJob.progress || 0,
          filename: serverJob.filename || '',
          recoveryHint: serverJob.recoveryHint,
          error: '',
        };
        const rebuilt = tryRebuildJob(skeleton);
        if (rebuilt) {
          enqueueRecovered(rebuilt);
        } else {
          setJobs((prev) => [{ ...skeleton, status: 'RECOVERING' }, ...prev]);
          pendingRecoveryRef.current.push(skeleton);
        }
      }
    },
    [jobs, tryRebuildJob, enqueueRecovered]
  );

  return (
    <PdfJobContext.Provider
      value={{
        jobs,
        addJob,
        clearJobs,
        removeJob,
        retryJob,
        downloadTrackedJobPdf,
        bootstrapServerJobs,
      }}
    >
      {children}
    </PdfJobContext.Provider>
  );
};
